import type { ImageModel } from 'ai';
import {
    BaseImageProvider,
    type ImageProviderAdapter,
    type ProviderContext,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getbaiduModelConfig } from './utils';
import type { baiduCreateResponse, baiduTaskResponse } from './types';
import { getValueByPath, isStatusMatch } from '../tools';

const DEFAULT_API_KEY_ENV = 'BAIDU_API_KEY';

const defaultPolling = {
    timeoutMs: 10 * 60 * 1000,
    backoff: {
        initialDelayMs: 5000,
        maxSteps: 20,
        scale: 1,
        jitter: 0.2,
        maxDelayMs: 10000,
    },
};

/**
 * 从响应数据中提取图像 URL 列表。
 * 兼容 string / string[] / { resultUrls } / { urls } 等多种格式。
 */
function extractImageUrlsFromValue(urlValue: any): string[] {
    if (typeof urlValue === 'string') {
        try {
            const parsed = JSON.parse(urlValue);
            if (parsed && Array.isArray(parsed.resultUrls)) {
                if (parsed.resultUrls.length > 0 && typeof parsed.resultUrls[0] === 'object' && parsed.resultUrls[0].resultUrl) {
                    return parsed.resultUrls.map((item: any) => item.resultUrl);
                }
                return parsed.resultUrls;
            }
            if (Array.isArray(parsed)) return parsed;
        } catch {
            return [urlValue];
        }
    }
    if (Array.isArray(urlValue)) {
        if (urlValue.length > 0 && typeof urlValue[0] === 'object' && (urlValue[0] as any).resultUrl) {
            return (urlValue as any[]).map((item: any) => item.resultUrl);
        }
        return urlValue;
    }
    if (urlValue && typeof urlValue === 'object') {
        if (Array.isArray((urlValue as any).resultUrls)) {
            const resultUrls = (urlValue as any).resultUrls;
            if (resultUrls.length > 0 && typeof resultUrls[0] === 'object' && resultUrls[0].resultUrl) {
                return resultUrls.map((item: any) => item.resultUrl);
            }
            return resultUrls;
        }
        if (Array.isArray((urlValue as any).urls)) return (urlValue as any).urls;
    }
    return [];
}

const providerCache = new Map<string, BaseImageProvider>();

function getOrCreateProvider(apiKeyEnv: string): BaseImageProvider {
    let provider = providerCache.get(apiKeyEnv);
    if (!provider) {
        provider = new BaseImageProvider(
            { baseUrlEnv: 'BAIDU_BASE_URL', apiKeyEnv, debugEnv: 'BAIDU_DEBUG', providerName: 'baidu' },
            defaultPolling,
        );
        providerCache.set(apiKeyEnv, provider);
    }
    return provider;
}

// 实现适配器
const adapter: ImageProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 获取服务商配置 (用于确定模型请求路径)
        const config = getbaiduModelConfig(modelId);
        // 构建输入参数 (可能是异步构建)
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 解析 API 路径（支持根据是否有图像输入动态切换，如 generations / edits）
        const image_urls: string[] = (params.providerOptions as any)?.url?.image_input ?? [];
        const apiPath = typeof config.apiPath === 'function'
            ? config.apiPath({ hasImage: image_urls.length > 0 })
            : config.apiPath;
        // FormData 请求体：不设置 Content-Type，交由 fetch 自动生成 multipart boundary
        const isFormData = payload instanceof FormData;
        // 返回请求配置
        return {
            url: `${context.baseUrl}${apiPath}`,
            method: 'POST',
            headers: {
                ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
                'Authorization': `Bearer ${context.apiKey}`,
                ...(config.extraHeaders ?? {}),
            },
            body: payload,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: baiduCreateResponse): CreateTaskResult {
        // 获取服务商配置 (用于确定 taskId 路径或同步返回)
        const config = getbaiduModelConfig(modelId);
        const statusMapping = config.statusMapping;

        // 同步模型：创建响应直接返回最终结果
        if (config.synchronous === true) {
            const state = getValueByPath(data, statusMapping.statePath);

            if (state !== undefined && isStatusMatch(state, statusMapping.success)) {
                const urlValue = getValueByPath(data, statusMapping.imageUrlPath);
                const imageUrls = extractImageUrlsFromValue(urlValue);
                if (imageUrls.length > 0) {
                    return { immediateImageUrls: imageUrls, raw: data };
                }
            }
            if (state !== undefined && isStatusMatch(state, statusMapping.failed)) {
                const error = getValueByPath(data, statusMapping.errorPath);
                return { error: String(error) || 'unknown error', raw: data };
            }
            const possibleUrlValue = getValueByPath(data, statusMapping.imageUrlPath);
            if (possibleUrlValue !== undefined) {
                const imageUrls = extractImageUrlsFromValue(possibleUrlValue);
                if (imageUrls.length > 0) {
                    return { immediateImageUrls: imageUrls, raw: data };
                }
            }
            // 否则退回异步流程（提取 taskId 开始轮询）
        }

        const taskIdPath = statusMapping.taskIdPath;
        // 获取任务 ID（异步）
        const taskId = getValueByPath(data, taskIdPath);
        if (!taskId) {
            const createError = getValueByPath(data, statusMapping.createErrorPath ?? statusMapping.errorPath);
            if (createError) {
                return { error: String(createError), raw: data };
            }
            throw new Error(`Failed to get task id from response`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 获取模型配置 (用于确定轮询路径)
        const config = getbaiduModelConfig(modelId);
        const pollApiPath = config.pollApiPath;
        // 返回请求配置
        // polling_url 模式: 直接使用 taskId 作为 URL
        if (!pollApiPath) {
            return {
                url: (taskId) => taskId,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${context.apiKey}`,
                },
            };
        }
        // 常规模式：拼接 baseUrl 与 pollApiPath
        return {
            url: (taskId) => {
                const url = pollApiPath.includes('{id}')
                    ? pollApiPath.replace('{id}', taskId)
                    : `${pollApiPath}/${taskId}`;
                return `${context.baseUrl}${url}`;
            },
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: baiduTaskResponse): PollTaskResult {
        const config = getbaiduModelConfig(modelId);
        const statusMapping = config.statusMapping;
        const state = getValueByPath(data, statusMapping.statePath);

        if (state === undefined) {
            return { status: TaskStatus.PROCESSING, raw: data };
        }

        // 成功
        if (isStatusMatch(state, statusMapping.success)) {
            // 有 resultApiPath → 需要二次拉取，直接返回空 imageUrls，基类会调用 buildFetchResultRequest
            if (config.resultApiPath) {
                return { status: TaskStatus.SUCCESS, imageUrls: [], raw: data };
            }
            // 无 resultApiPath → 直接从当前响应提取图像 URL
            const urlValue = getValueByPath(data, statusMapping.imageUrlPath);
            if (!urlValue) {
                return { status: TaskStatus.FAILED, error: 'Missing image URL in poll response', raw: data };
            }
            return { status: TaskStatus.SUCCESS, imageUrls: extractImageUrlsFromValue(urlValue), raw: data };
        }

        // 失败
        if (isStatusMatch(state, statusMapping.failed)) {
            const error = getValueByPath(data, statusMapping.errorPath);
            return { status: TaskStatus.FAILED, error: String(error) || 'unknown error', raw: data };
        }

        // 处理中（含未知状态兜底）
        return { status: TaskStatus.PROCESSING, raw: data };
    },

    // 构建**结果拉取**请求（二步轮询）
    buildFetchResultRequest(modelId, taskId, context): CreateTaskConfig | null {
        const config = getbaiduModelConfig(modelId);
        if (!config.resultApiPath) return null;

        const path = config.resultApiPath.includes('{id}')
            ? config.resultApiPath.replace('{id}', taskId)
            : `${config.resultApiPath}/${taskId}`;

        return {
            url: `${context.baseUrl}${path}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Key ${context.apiKey}`,
            },
        };
    },

    // 解析**结果拉取**响应（成功 / 失败均处理）
    parseFetchResultResponse(modelId, response, data): PollTaskResult {
        const config = getbaiduModelConfig(modelId);
        const { errorPath, imageUrlPath } = config.statusMapping;

        if (!response.ok) {
            const error = getValueByPath(data, errorPath);
            return { status: TaskStatus.FAILED, error: error ? String(error) : `HTTP ${response.status}`, raw: data };
        }

        const urlValue = getValueByPath(data, imageUrlPath);
        if (!urlValue) {
            return { status: TaskStatus.FAILED, error: 'Missing image URL in result response', raw: data };
        }
        const imageUrls = extractImageUrlsFromValue(urlValue);
        if (imageUrls.length === 0) {
            return { status: TaskStatus.FAILED, error: 'Failed to parse image URLs from result response', raw: data };
        }
        return { status: TaskStatus.SUCCESS, imageUrls, raw: data };
    },
};

// 导出工厂函数
export const baidu = {
    image: (modelId: string): ImageModel => {
        const config = getbaiduModelConfig(modelId);
        const apiKeyEnv = config.apiKeyEnv ?? DEFAULT_API_KEY_ENV;
        const provider = getOrCreateProvider(apiKeyEnv);
        return provider.createImageModel(modelId, adapter, config.timeoutMs);
    },
};